import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Permission, PermissionQueryParams, PermissionState, CreatePermissionData, UpdatePermissionData } from '@/types/permission';
import { permissionService } from '@/services/permissionService';

const initialState: PermissionState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  loading: false,
  error: null,
};

export const fetchPermissions = createAsyncThunk(
  'permission/fetchPermissions',
  async (params: PermissionQueryParams) => {
    const response = await permissionService.getPermissions(params);
    return response;
  }
);

export const createPermission = createAsyncThunk(
  'permission/createPermission',
  async (data: CreatePermissionData) => {
    const response = await permissionService.createPermission(data);
    return response;
  }
);

export const updatePermission = createAsyncThunk(
  'permission/updatePermission',
  async ({ id, data }: { id: number; data: UpdatePermissionData }) => {
    const response = await permissionService.updatePermission(id, data);
    return response;
  }
);

const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    setPagination: (state, action) => {
      if (state.page !== action.payload.page || state.pageSize !== action.payload.pageSize) {
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.size;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取权限列表失败';
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.total += 1;
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      });
  },
});

export default permissionSlice.reducer;
